import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  LinearProgress,
  Tab,
  Tabs,
  IconButton,
  Tooltip,
  Alert,
  Stack,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  ViewModule as ModuleIcon,
  Assignment as ActivityIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  CloudDone as CloudIcon,
  Code as CodeIcon,
  Assessment as AssessmentIcon,
  Description as PWSIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { odosModuleService, ODOSModule, ODOSDashboardStats, odosActivityService, ODOSActivityStats } from '../../services/odosService';
import { useAuth } from '../../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const CATEGORY_COLORS: { [key: string]: string } = {
  'DevSecOps': '#2196f3',
  'Security & Compliance': '#f44336',
  'Modernization': '#9c27b0',
  'Monitoring & Observability': '#ff9800',
  'Data Integration': '#4caf50',
  'User Experience': '#00bcd4',
  'Business Process': '#3f51b5',
  'Data Governance': '#8bc34a',
  'Infrastructure': '#607d8b',
  'Access Management': '#e91e63',
  'Collaboration': '#673ab7',
  'Quality Assurance': '#009688',
  'Accessibility': '#ffc107',
  'Administration': '#795548'
};

const CATEGORY_ICONS: { [key: string]: React.ReactElement } = {
  'DevSecOps': <CodeIcon />,
  'Security & Compliance': <SecurityIcon />,
  'Modernization': <CloudIcon />,
  'Monitoring & Observability': <AssessmentIcon />,
  'Data Integration': <ModuleIcon />,
  'User Experience': <DashboardIcon />,
  'Business Process': <ActivityIcon />,
  'Data Governance': <SecurityIcon />,
  'Infrastructure': <CloudIcon />,
  'Access Management': <SecurityIcon />,
  'Collaboration': <ModuleIcon />,
  'Quality Assurance': <AssessmentIcon />,
  'Accessibility': <DashboardIcon />,
  'Administration': <ModuleIcon />
};

const ODOSDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<ODOSDashboardStats | null>(null);
  const [activityStats, setActivityStats] = useState<ODOSActivityStats | null>(null);
  const [activityCounts, setActivityCounts] = useState<{ [moduleId: string]: number }>({});
  const [modules, setModules] = useState<ODOSModule[]>([]);
  const [userModules, setUserModules] = useState<ODOSModule[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [statsResponse, modulesResponse, userModulesResponse, activityStatsResponse, activitiesResponse] = await Promise.all([
        odosModuleService.getDashboardStats(),
        odosModuleService.getModules(),
        odosModuleService.getUserModules(),
        odosActivityService.getActivityStats(),
        odosActivityService.getActivities()
      ]);
      
      setStats(statsResponse.data);
      setModules(modulesResponse.data);
      setUserModules(userModulesResponse.data);
      setActivityStats(activityStatsResponse.data);
      
      // Count activities by module
      const counts: { [moduleId: string]: number } = {};
      activitiesResponse.data.forEach((activity: any) => {
        let moduleId: string | null = null;
        
        if (activity.module) {
          if (typeof activity.module === 'object' && activity.module._id) {
            moduleId = activity.module._id;
          } else if (typeof activity.module === 'string') {
            moduleId = activity.module;
          }
        }
        
        if (moduleId) {
          counts[moduleId] = (counts[moduleId] || 0) + 1;
        }
      });
      setActivityCounts(counts);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load ODOS data');
      console.error('Error loading ODOS data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'in-development': return 'info';
      case 'testing': return 'warning';
      case 'planning': return 'default';
      case 'deployed': return 'success';
      case 'maintenance': return 'warning';
      case 'deprecated': return 'error';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return '#d32f2f';
      case 'high': return '#f57c00';
      case 'medium': return '#1976d2';
      case 'low': return '#388e3c';
      default: return '#757575';
    }
  };

  const filteredModules = selectedCategory
    ? modules.filter(m => m.category === selectedCategory)
    : modules;
  
  // Sort modules by sortOrder (priority ranking) or by name as fallback
  const sortedModules = [...filteredModules].sort((a, b) => {
    // First by sortOrder if it exists
    if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
      return a.sortOrder - b.sortOrder;
    }
    // Fallback to name
    return a.name.localeCompare(b.name);
  });

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2, textAlign: 'center' }}>Loading ODOS Dashboard...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#003366', fontFamily: 'Merriweather, serif' }}>
            ODOS-IV Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            DHS USCIS Outcome-Driven Operations and Support
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => window.open('/security/docs?doc=E-UNIFY-BENEFITS.md&back=/odos', '_blank')}
            sx={{ borderColor: '#003366', color: '#003366' }}
          >
            View Benefits
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/odos/activities')}
            sx={{ borderColor: '#003366', color: '#003366' }}
          >
            View All Activities
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/security/docs?doc=E-UNIFY-BENEFITS.md&back=/odos')}
            sx={{ borderColor: '#003366', color: '#003366' }}
          >
            Benefits
          </Button>
          <Tooltip title="Refresh Data">
            <IconButton onClick={loadData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/odos/modules/new')}
            sx={{ ml: 1, bgcolor: '#003366', '&:hover': { bgcolor: '#002244' } }}
          >
            New Module
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#003366', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>Total Modules</Typography>
                <Typography variant="h3" sx={{ color: 'white' }}>{stats.totalModules}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#4caf50', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>Active</Typography>
                <Typography variant="h3" sx={{ color: 'white' }}>{stats.activeModules}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#2196f3', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>In Development</Typography>
                <Typography variant="h3" sx={{ color: 'white' }}>{stats.inDevelopment}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: '#ff9800', color: 'white' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>Planning</Typography>
                <Typography variant="h3" sx={{ color: 'white' }}>{stats.planning}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* PWS Document Card */}
      <Card
        sx={{
          mb: 4,
          bgcolor: '#B31B1B',
          color: 'white',
          cursor: 'pointer',
          transition: 'all 0.3s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6
          }
        }}
        onClick={() => navigate('/odos/pws')}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PWSIcon sx={{ fontSize: 48, color: 'white' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'white', mb: 1 }}>
                ODOS IV Performance Work Statement (PWS)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View and edit the complete PWS document with 68 pages of requirements, scope, and objectives
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label="68 Pages" size="small" sx={{ bgcolor: 'white', color: '#B31B1B' }} />
                <Chip label="Industry Day 11/4/2024" size="small" sx={{ bgcolor: 'white', color: '#B31B1B' }} />
                <Chip label="Click to Edit" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.3)', color: 'white' }} />
              </Box>
            </Box>
            <Button
              variant="contained"
              sx={{
                bgcolor: 'white',
                color: '#B31B1B',
                fontWeight: 'bold',
                '&:hover': { bgcolor: '#f0f0f0' }
              }}
            >
              Open Document
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Category Filter Chips */}
      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <Chip
          label="All Categories"
          onClick={() => setSelectedCategory(null)}
          color={selectedCategory === null ? 'primary' : 'default'}
          sx={{ fontWeight: selectedCategory === null ? 'bold' : 'normal' }}
        />
        {stats?.categories.map((cat) => (
          <Chip
            key={cat._id}
            label={`${cat._id} (${cat.count})`}
            onClick={() => setSelectedCategory(cat._id)}
            color={selectedCategory === cat._id ? 'primary' : 'default'}
            sx={{
              fontWeight: selectedCategory === cat._id ? 'bold' : 'normal',
              bgcolor: selectedCategory === cat._id ? CATEGORY_COLORS[cat._id] : undefined,
              color: selectedCategory === cat._id ? 'white' : undefined
            }}
          />
        ))}
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="ODOS tabs">
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>All Modules</span>
                <Chip 
                  label={sortedModules.length} 
                  size="small" 
                  sx={{ 
                    bgcolor: '#003366', 
                    color: 'white',
                    height: '20px',
                    fontSize: '0.75rem'
                  }} 
                />
              </Box>
            }
            icon={<ModuleIcon />} 
            iconPosition="start" 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>My Modules</span>
                <Chip 
                  label={userModules.length} 
                  size="small" 
                  sx={{ 
                    bgcolor: '#2E7D32', 
                    color: 'white',
                    height: '20px',
                    fontSize: '0.75rem'
                  }} 
                />
              </Box>
            }
            icon={<DashboardIcon />} 
            iconPosition="start" 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Activities</span>
                <Chip 
                  label={activityStats?.totalActivities || 0} 
                  size="small" 
                  sx={{ 
                    bgcolor: '#B31B1B', 
                    color: 'white',
                    height: '20px',
                    fontSize: '0.75rem'
                  }} 
                />
              </Box>
            }
            icon={<ActivityIcon />} 
            iconPosition="start" 
          />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {sortedModules.map((module) => (
            <Grid item xs={12} md={6} lg={4} key={module._id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderTop: `4px solid ${CATEGORY_COLORS[module.category] || '#003366'}`,
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s'
                  },
                  '&:focus': {
                    outline: '2px solid #003366',
                    outlineOffset: '2px'
                  }
                }}
                onClick={() => navigate(`/odos/modules/${module._id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/odos/modules/${module._id}`);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={`View details for ${module.name} module`}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ color: CATEGORY_COLORS[module.category] || '#003366', mr: 1 }}>
                      {CATEGORY_ICONS[module.category] || <ModuleIcon />}
                    </Box>
                    <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
                      {module.name}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                    {module.description.substring(0, 120)}...
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={module.moduleId}
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                    />
                    <Chip
                      label={module.category}
                      size="small"
                      sx={{
                        mr: 1,
                        mb: 1,
                        bgcolor: CATEGORY_COLORS[module.category] || '#003366',
                        color: 'white'
                      }}
                    />
                    <Chip
                      label={module.status}
                      size="small"
                      color={getStatusColor(module.status)}
                      sx={{ mb: 1 }}
                    />
                  </Box>
                  
                  {module.features && module.features.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Progress: {module.completionPercentage || 0}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={module.completionPercentage || 0}
                        sx={{ mt: 0.5 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {module.features.filter(f => f.status === 'completed').length} of {module.features.length} features completed
                      </Typography>
                    </Box>
                  )}
                </CardContent>
                
                <CardActions>
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/odos/modules/${module._id}`);
                    }}
                    sx={{ color: '#003366' }}
                  >
                    View Details
                  </Button>
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/odos/modules/${module._id}/activities`);
                    }}
                  >
                    Activities ({activityCounts[module._id] || 0})
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        {filteredModules.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <ModuleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No modules found
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/odos/modules/new')}
              sx={{ mt: 2, bgcolor: '#003366' }}
            >
              Create First Module
            </Button>
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          {userModules.map((module) => (
            <Grid item xs={12} md={6} lg={4} key={module._id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderTop: `4px solid ${CATEGORY_COLORS[module.category] || '#003366'}`,
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-4px)',
                    transition: 'all 0.3s'
                  },
                  '&:focus': {
                    outline: '2px solid #003366',
                    outlineOffset: '2px'
                  }
                }}
                onClick={() => navigate(`/odos/modules/${module._id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/odos/modules/${module._id}`);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={`View details for ${module.name} module`}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ color: CATEGORY_COLORS[module.category] || '#003366', mr: 1 }}>
                      {CATEGORY_ICONS[module.category] || <ModuleIcon />}
                    </Box>
                    <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
                      {module.name}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                    {module.description.substring(0, 120)}...
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={module.moduleId}
                      size="small"
                      sx={{ mr: 1, mb: 1 }}
                    />
                    <Chip
                      label={module.category}
                      size="small"
                      sx={{
                        mr: 1,
                        mb: 1,
                        bgcolor: CATEGORY_COLORS[module.category] || '#003366',
                        color: 'white'
                      }}
                    />
                    <Chip
                      label={module.status}
                      size="small"
                      color={getStatusColor(module.status)}
                      sx={{ mb: 1 }}
                    />
                  </Box>
                  
                  {/* Show user's role in this module */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Your Role: {
                        module.team?.lead === user?._id ? 'Team Lead' :
                        module.team?.members?.some(m => m.user === user?._id) ? 
                          module.team.members.find(m => m.user === user?._id)?.role || 'Team Member' :
                          'Creator'
                      }
                    </Typography>
                  </Box>
                  
                  {module.features && module.features.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Progress: {module.completionPercentage || 0}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={module.completionPercentage || 0}
                        sx={{ mt: 0.5 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {module.features.filter(f => f.status === 'completed').length} of {module.features.length} features completed
                      </Typography>
                    </Box>
                  )}
                </CardContent>
                
                <CardActions>
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/odos/modules/${module._id}`);
                    }}
                    sx={{ color: '#003366' }}
                  >
                    View Details
                  </Button>
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/odos/modules/${module._id}/activities`);
                    }}
                  >
                    Activities ({activityCounts[module._id] || 0})
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        {userModules.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <DashboardIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No modules assigned to you
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You haven't been assigned to any ODOS modules yet.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setTabValue(0)}
              sx={{ borderColor: '#003366', color: '#003366' }}
            >
              View All Modules
            </Button>
          </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          {/* Recent Activities Section */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ color: '#003366', fontFamily: 'Merriweather, serif' }}>
                    Recent Activities
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => navigate('/odos/activities')}
                    sx={{ borderColor: '#003366', color: '#003366' }}
                  >
                    View All Activities
                  </Button>
                </Box>

                {stats?.recentActivities && stats.recentActivities.length > 0 ? (
                  <List>
                    {stats.recentActivities.slice(0, 5).map((activity: any, index: number) => (
                      <React.Fragment key={activity._id || index}>
                        <ListItem
                          sx={{
                            px: 0,
                            py: 1,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: '#f5f5f5' }
                          }}
                          onClick={() => navigate(`/odos/activities/${activity._id}`)}
                        >
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: getStatusColor(activity.status || 'planned'), width: 40, height: 40 }}>
                              <ActivityIcon sx={{ fontSize: 20 }} />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {activity.title || 'Untitled Activity'}
                                </Typography>
                                <Chip
                                  label={activity.status || 'planned'}
                                  size="small"
                                  color={getStatusColor(activity.status || 'planned')}
                                  sx={{ fontSize: '0.7rem' }}
                                />
                                {(activity.priority) && (
                                  <Chip
                                    label={activity.priority}
                                    size="small"
                                    sx={{
                                      fontSize: '0.7rem',
                                      bgcolor: getPriorityColor(activity.priority),
                                      color: 'white'
                                    }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Typography variant="body2" color="text.secondary">
                                {activity.module && typeof activity.module === 'object' ? activity.module.name : 'Unknown Module'} • Updated {activity.updatedAt ? new Date(activity.updatedAt).toLocaleDateString() : 'Recently'}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {index < Math.min(stats.recentActivities.length, 5) - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <ActivityIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      No recent activities found
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Activity Stats & Quick Actions */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              {/* Activity Stats Cards */}
              {activityStats && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontFamily: 'Merriweather, serif' }}>
                      Activity Overview
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="success.main">
                            {activityStats?.totalActivities || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Total
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="info.main">
                            {activityStats?.inProgress || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            In Progress
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="error.main">
                            {activityStats?.blocked || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Blocked
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" color="success.main">
                            {activityStats?.completed || 0}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Completed
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Quick Actions */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: '#003366', fontFamily: 'Merriweather, serif' }}>
                    Quick Actions
                  </Typography>
                  <Stack spacing={1}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => navigate('/odos/activities/new')}
                      sx={{ justifyContent: 'flex-start', borderColor: '#003366', color: '#003366' }}
                    >
                      Create New Activity
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<ActivityIcon />}
                      onClick={() => navigate('/odos/activities')}
                      sx={{ justifyContent: 'flex-start', borderColor: '#003366', color: '#003366' }}
                    >
                      Manage Activities
                    </Button>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<DashboardIcon />}
                      onClick={() => navigate('/odos')}
                      sx={{ justifyContent: 'flex-start', borderColor: '#003366', color: '#003366' }}
                    >
                      Back to Dashboard
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>
      </TabPanel>
    </Container>
  );
};

export default ODOSDashboard;
